import { useEffect, useRef, useCallback } from 'react';
import { brainNotificationsAPI } from '../../lib/api';
import { toast } from 'sonner';

const EVENT_ICONS = {
  decision: 'brain',
  email: 'mail',
  task: 'check',
  lead_scored: 'zap',
  pipeline_ready: 'target',
  cold_lead: 'alert',
};

const EVENT_LABELS = {
  decision: 'Brain Decision',
  email: 'Email Activity',
  task: 'Task Created',
  lead_scored: 'Lead Scored',
  pipeline_ready: 'Pipeline Ready',
  cold_lead: 'Cold Lead Alert',
};

export default function BrainNotificationListener() {
  const lastCheck = useRef(new Date().toISOString()); // Only show alerts AFTER mount
  const intervalRef = useRef(null);

  const poll = useCallback(async () => {
    try {
      const res = await brainNotificationsAPI.poll(lastCheck.current);
      const data = res.data;

      if (data.has_new) {
        // Show toast for new events
        const events = data.latest_events || [];
        events.slice(0, 3).forEach((evt) => {
          const label = EVENT_LABELS[evt.type] || 'Brain Activity';
          toast.info(evt.action || label, {
            description: label,
            duration: 5000,
          });
        });

        // Show toast for new alerts (filter out sample/seeded data alerts)
        const alerts = data.latest_alerts || [];
        alerts
          .filter((alert) => !alert.title?.includes('Initialized') && !alert.title?.includes('Sample'))
          .slice(0, 2)
          .forEach((alert) => {
            const severity = alert.severity === 'warning' ? toast.warning : alert.severity === 'error' ? toast.error : toast.info;
            severity(alert.title || 'Brain Alert', {
              description: alert.message?.substring(0, 100),
              duration: 7000,
            });
          });
      }

      lastCheck.current = data.checked_at || new Date().toISOString();
    } catch {
      // Silent fail on poll errors
    }
  }, []);

  useEffect(() => {
    // Initial poll after 5s
    const timeout = setTimeout(poll, 5000);
    // Then poll every 30s
    intervalRef.current = setInterval(poll, 30000);

    return () => {
      clearTimeout(timeout);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [poll]);

  return null; // Invisible component — just listens
}
